import { useCallback } from 'react';
import type { ChatContextRef, Message, StructuredMessagePayload } from '@govard/ai-chat-kit';

import {
  commandRequest,
  type UiCommandRequest,
} from '../../../agents/context/ui-command-registry.js';

export type EmailDraft = Extract<StructuredMessagePayload, { type: 'email_draft' }>['draft'];
export type DealActionPlan = Extract<
  StructuredMessagePayload,
  { type: 'deal_action_plan' }
>['plan'];
export type MeetingOutcomeMeeting = Extract<
  StructuredMessagePayload,
  { type: 'meeting_outcome' }
>['meeting'];

export interface MeetingTaskCandidate {
  title: string;
  rationale?: string | null;
}

export interface UseChatActionHandlersOptions {
  agent: {
    messages: Message[];
    send: (
      request: string | UiCommandRequest,
      options?: { contextRefs?: ChatContextRef[] },
    ) => Promise<void>;
  };
  dealId?: string | undefined;
  selectedDeal?: { id: string; label?: string } | null | undefined;
  onDealSelected?: ((deal: { id: string; label: string }) => void) | undefined;
}

export function useChatActionHandlers({
  agent,
  dealId,
  selectedDeal,
  onDealSelected,
}: UseChatActionHandlersOptions) {
  const targetDealId = useCallback(
    (explicit?: string) => explicit ?? dealId ?? selectedDeal?.id,
    [dealId, selectedDeal?.id],
  );
  const sourceMessage = useCallback(
    (
      type: StructuredMessagePayload['type'],
      predicate?: (payload: StructuredMessagePayload) => boolean,
    ) => {
      for (const message of [...(agent.messages ?? [])].reverse()) {
        let payload = message.structuredPayload;
        if (!payload) {
          try {
            payload = JSON.parse(message.content) as StructuredMessagePayload;
          } catch {
            continue;
          }
        }
        if (payload?.type === type && (!predicate || predicate(payload)))
          return { message, payload };
      }
      return undefined;
    },
    [agent.messages],
  );
  const onSendEmailDraft = useCallback(
    (draft?: EmailDraft): void => {
      const source = sourceMessage(
        'email_draft',
        (payload) =>
          payload.type === 'email_draft' &&
          (!draft || JSON.stringify(payload.draft) === JSON.stringify(draft)),
      );
      const id = targetDealId();
      if (source && id)
        void agent.send(
          commandRequest('meetingSend', { dealId: id, sourceMessageId: source.message.id }),
        );
    },
    [agent, sourceMessage, targetDealId],
  );
  const onPrepareDealFollowUp = useCallback(
    (id: string): void => {
      onDealSelected?.({ id, label: selectedDeal?.label ?? id });
      void agent.send(commandRequest('prepareFollowUp', { dealId: id }));
    },
    [agent, onDealSelected, selectedDeal],
  );
  const onProposeTouchStrategy = useCallback(
    (id: string): void => {
      onDealSelected?.({ id, label: selectedDeal?.label ?? id });
      void agent.send(commandRequest('touchStrategy', { dealId: id }));
    },
    [agent, onDealSelected, selectedDeal],
  );
  const onCreateDealTasks = useCallback(
    (plan: DealActionPlan, actions: DealActionPlan['actions']): void => {
      const source = sourceMessage(
        'deal_action_plan',
        (payload) =>
          payload.type === 'deal_action_plan' &&
          JSON.stringify(payload.plan) === JSON.stringify(plan),
      );
      if (!source) return;
      const selectedActionIds = actions.map(
        (action) =>
          `action:${plan.actions.indexOf(action) >= 0 ? plan.actions.indexOf(action) : plan.actions.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(action))}`,
      );
      void agent.send(
        commandRequest('createPlanTasks', {
          dealId: plan.deal.id,
          sourceMessageId: source.message.id,
          selectedActionIds,
        }),
      );
    },
    [agent, sourceMessage],
  );
  const onCreateMeetingTasks = useCallback(
    (meeting: MeetingOutcomeMeeting, actions: readonly MeetingTaskCandidate[]): void => {
      const source = sourceMessage(
        'meeting_outcome',
        (payload) =>
          payload.type === 'meeting_outcome' &&
          JSON.stringify(payload.meeting) === JSON.stringify(meeting),
      );
      if (!source) return;
      const selectedActionIds = actions.map(
        (action) =>
          `action:${meeting.nextActions?.findIndex((candidate) => candidate.title === action.title) ?? -1}`,
      );
      void agent.send(
        commandRequest('meetingTasks', {
          dealId: meeting.deal.id,
          sourceMessageId: source.message.id,
          selectedActionIds,
        }),
      );
    },
    [agent, sourceMessage],
  );
  const onPrepareMeetingFollowUp = useCallback(
    (meeting?: MeetingOutcomeMeeting): void => {
      const source = sourceMessage(
        'meeting_outcome',
        (payload) =>
          payload.type === 'meeting_outcome' &&
          (!meeting || JSON.stringify(payload.meeting) === JSON.stringify(meeting)),
      );
      const id = targetDealId(meeting?.deal.id);
      if (source && id)
        void agent.send(
          commandRequest('meetingFollowUp', { dealId: id, sourceMessageId: source.message.id }),
        );
    },
    [agent, sourceMessage, targetDealId],
  );
  return {
    onSendEmailDraft,
    onPrepareDealFollowUp,
    onProposeTouchStrategy,
    onCreateDealTasks,
    onCreateMeetingTasks,
    onPrepareMeetingFollowUp,
  };
}
